import { useState } from 'react'
import { useAuth } from '../context/AuthContext'
import { deleteUserProfile, updateUserCurrency } from '../services/userService'
import { deleteUser, signOut } from 'firebase/auth'
import { auth } from '../services/firebase'

const CURRENCIES = [
    { value: 'USD', label: 'US Dollar', symbol: '$' },
    { value: 'EUR', label: 'Euro', symbol: '€' },
    { value: 'GBP', label: 'British Pound', symbol: '£' },
    { value: 'RSD', label: 'Serbian Dinar', symbol: 'din' }
]

function ProfilePage() {
    const { user, userProfile, logout } = useAuth()
    const [currency, setCurrency] = useState(userProfile?.currency || 'USD')
    const [isSaving, setIsSaving] = useState(false)
    const [saveSuccess, setSaveSuccess] = useState(false)
    const [showDeleteModal, setShowDeleteModal] = useState(false)
    const [isDeleting, setIsDeleting] = useState(false)

    const handleCurrencyChange = async (newCurrency) => {
        setCurrency(newCurrency)
        setIsSaving(true)
        setSaveSuccess(false)

        try {
            await updateUserCurrency(user.uid, newCurrency)
            setSaveSuccess(true)
            setTimeout(() => setSaveSuccess(false), 3000)
        } catch (error) {
            console.error('Failed to update currency:', error)
        } finally {
            setIsSaving(false)
        }
    }

    const handleDeleteAccount = async () => {
        setIsDeleting(true)
        try {
            console.log('Initiating account deletion for:', user.uid)

            // 1. Delete Firestore profile
            await deleteUserProfile(user.uid)

            // 2. Delete Auth user (requires recent login)
            const firebaseUser = auth.currentUser
            if (firebaseUser) {
                await deleteUser(firebaseUser)
            }

            console.log('Account deleted successfully')
            await logout()
        } catch (error) {
            console.error('Failed to delete account:', error)

            if (error.code === 'auth/requires-recent-login') {
                alert('For security reasons, this action requires you to have logged in recently. Please log out and back in, then try again.')
            } else {
                alert('An error occurred while deleting your account. Please try again later.')
            }
        } finally {
            setIsDeleting(false)
            setShowDeleteModal(false)
        }
    }

    const getRoleBadge = (role) => {
        switch (role) {
            case 'admin':
                return { label: 'Administrator', class: 'admin' }
            case 'paid':
                return { label: 'Premium', class: 'paid' }
            default:
                return { label: 'Free', class: 'free' }
        }
    }

    const roleBadge = getRoleBadge(userProfile?.role)

    return (
        <div className="profile-page">
            <div className="container">
                <div className="page-header">
                    <h1>Profile Settings</h1>
                    <p>Manage your account preferences</p>
                </div>

                <div className="profile-grid">
                    {/* Account Info */}
                    <section className="profile-section card-glass">
                        <h2>Account Information</h2>

                        <div className="info-group">
                            <label>Email Address</label>
                            <div className="info-value">{user?.email}</div>
                        </div>

                        <div className="info-group">
                            <label>Account Type</label>
                            <div className="info-value">
                                <span className={`role-badge ${roleBadge.class}`}>
                                    {roleBadge.label}
                                </span>
                                {userProfile?.trialEndsAt && userProfile?.role === 'paid' && (
                                    <span className="trial-info">
                                        Trial ends: {new Date(userProfile.trialEndsAt.toDate?.() || userProfile.trialEndsAt).toLocaleDateString()}
                                    </span>
                                )}
                            </div>
                        </div>

                        <div className="info-group">
                            <label>Member Since</label>
                            <div className="info-value">
                                {userProfile?.createdAt
                                    ? new Date(userProfile.createdAt.toDate?.() || userProfile.createdAt).toLocaleDateString()
                                    : 'N/A'
                                }
                            </div>
                        </div>
                    </section>

                    {/* Currency Settings */}
                    <section className="profile-section card-glass">
                        <h2>Display Preferences</h2>

                        <div className="setting-group">
                            <label htmlFor="currency">Default Currency</label>
                            <p className="setting-description">
                                All identification results and price estimates will be displayed in this currency.
                            </p>

                            <div className="currency-selector">
                                {CURRENCIES.map(curr => (
                                    <button
                                        key={curr.value}
                                        className={`currency-option ${currency === curr.value ? 'selected' : ''}`}
                                        onClick={() => handleCurrencyChange(curr.value)}
                                        disabled={isSaving}
                                    >
                                        <span className="currency-symbol">{curr.symbol}</span>
                                        <span className="currency-label">{curr.label}</span>
                                    </button>
                                ))}
                            </div>

                            {saveSuccess && (
                                <div className="save-success">
                                    ✓ Currency preference saved
                                </div>
                            )}
                        </div>
                    </section>

                    {/* Data & Privacy */}
                    <section className="profile-section card-glass">
                        <h2>Data & Privacy</h2>

                        <div className="policy-info">
                            <h4>Data Retention Policy</h4>
                            <p>
                                Your uploaded images and identification history are stored securely to provide
                                our services and improve identification accuracy. You agreed to our data retention
                                policy upon registration.
                            </p>
                            <a href="/privacy" className="policy-link">View Full Privacy Policy →</a>
                        </div>

                        <div className="danger-zone">
                            <h4>Delete Account</h4>
                            <p>
                                Permanently delete your account and all associated personal data.
                                Note: Identification data may be retained for service improvement as per our policy.
                            </p>
                            <button
                                className="btn btn-danger"
                                onClick={() => setShowDeleteModal(true)}
                            >
                                Delete My Account
                            </button>
                        </div>
                    </section>
                </div>

                {/* Delete Confirmation Modal */}
                {showDeleteModal && (
                    <div className="modal-overlay" onClick={() => setShowDeleteModal(false)}>
                        <div className="modal-content card-glass" onClick={e => e.stopPropagation()}>
                            <h2 className="modal-title text-gold">Delete Account?</h2>

                            <div className="modal-body">
                                <p className="warning-text">
                                    ⚠️ This action cannot be undone.
                                </p>
                                <p>
                                    By deleting your account:
                                </p>
                                <ul className="delete-consequences">
                                    <li>Your personal data will be permanently removed</li>
                                    <li>You will lose access to your identification history</li>
                                    <li>Your uploaded images will be deleted</li>
                                    <li>Identification data may be retained anonymously for service improvement</li>
                                </ul>
                            </div>

                            <div className="modal-actions">
                                <button
                                    className="btn btn-secondary"
                                    onClick={() => setShowDeleteModal(false)}
                                >
                                    Cancel
                                </button>
                                <button
                                    className="btn btn-danger"
                                    onClick={handleDeleteAccount}
                                >
                                    Yes, Delete My Account
                                </button>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

export default ProfilePage
